export interface MenuHubModeCard {
  index: number
  badge: string
  title: string
  body: string
  meta: string
  tone: 'story' | 'survival' | 'daily'
  onTap: () => void
}

export interface MenuHubMiniCard {
  index: number
  label: string
  sub: string
  tone: 'collection' | 'progress' | 'shop'
  onTap: () => void
}

export interface MenuHubOverlayOptions {
  title: string
  subtitle: string
  accountLine: string
  todayLabel: string
  pitch: string
  footer: string
  modes: MenuHubModeCard[]
  minis: MenuHubMiniCard[]
  gachaLabel: string
  settingsLabel: string
  onGacha: () => void
  onSettings: () => void
  onAccount: () => void
}

export interface MenuHubOverlayHandle {
  setAccountLine: (text: string) => void
  destroy: () => void
}

export function mountMenuHubOverlay(options: MenuHubOverlayOptions): MenuHubOverlayHandle {
  const root = document.createElement('div')
  root.className = 'game-menu-overlay'

  const panel = document.createElement('section')
  panel.className = 'game-menu-panel'

  const header = document.createElement('header')
  header.className = 'game-menu-header'
  const title = document.createElement('h1')
  title.textContent = options.title
  const subtitle = document.createElement('p')
  subtitle.textContent = options.subtitle
  const account = document.createElement('button')
  account.type = 'button'
  account.className = 'game-menu-account'
  account.textContent = options.accountLine
  account.hidden = !options.accountLine
  account.addEventListener('click', options.onAccount)
  header.append(title, subtitle, account)

  const intro = document.createElement('section')
  intro.className = 'game-menu-intro'
  const today = document.createElement('strong')
  today.textContent = options.todayLabel
  const pitch = document.createElement('p')
  pitch.textContent = options.pitch
  intro.append(today, pitch)

  const modes = document.createElement('div')
  modes.className = 'game-menu-modes'
  for (const mode of options.modes) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = `game-menu-mode is-${mode.tone}`
    btn.addEventListener('click', mode.onTap)
    btn.innerHTML = [
      '<span class="game-menu-mode-badge">',
      `${escapeHtml(String(mode.index + 1))}. ${escapeHtml(mode.badge)}`,
      '</span>',
      `<strong>${escapeHtml(mode.title)}</strong>`,
      `<em>${escapeHtml(mode.body)}</em>`,
      `<b>${escapeHtml(mode.meta)}</b>`,
    ].join('')
    modes.append(btn)
  }

  const minis = document.createElement('div')
  minis.className = 'game-menu-minis'
  for (const mini of options.minis) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = `game-menu-mini is-${mini.tone}`
    btn.addEventListener('click', mini.onTap)
    btn.innerHTML = [
      `<strong>${escapeHtml(String(mini.index + 1))}. ${escapeHtml(mini.label)}</strong>`,
      `<span>${escapeHtml(mini.sub)}</span>`,
    ].join('')
    minis.append(btn)
  }

  const actions = document.createElement('div')
  actions.className = 'game-menu-actions'
  actions.append(
    button(options.gachaLabel, 'game-menu-action', options.onGacha),
    button(options.settingsLabel, 'game-menu-action', options.onSettings),
  )

  const footer = document.createElement('p')
  footer.className = 'game-menu-footer'
  footer.textContent = options.footer

  panel.append(header, intro, modes, minis, actions, footer)
  root.append(panel)
  document.body.append(root)

  return {
    setAccountLine: (text) => {
      account.textContent = text
      account.hidden = !text
    },
    destroy: () => root.remove(),
  }
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = className
  btn.textContent = label
  btn.addEventListener('click', onClick)
  return btn
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
